import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';
import NoImg from '../assets/images/no-image-icon-23485.png';
import ReactMarkdown from "react-markdown";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface Step {
  id: number;
  order: number;
  place: string;
  place_name: string;
  activity: string;
  start_time: string | null;
  duration_minutes: number;
  suggestion: string;
  first_image_url?: string | null;
}

export interface Plan {
  id: number;
  name: string;
  description: string;
  suggested_season: string;
  suggested_months: string;
  created_at: string;
  steps: Step[];
}

interface PlanCardProps {
  plan: Plan;
}

const formatTime = (timeString: string | null) => {
  if (!timeString) return '--:--';
  return timeString.split(':').slice(0, 2).join(':');
};

const getImageUrl = (fileUrl?: string | null) => {
  if (!fileUrl) return NoImg;
  if (fileUrl.startsWith('http')) return fileUrl;
  return `${BASE_URL}${fileUrl}`;
};

const PlanCard: React.FC<PlanCardProps> = ({ plan }) => {
  const images = plan.steps.slice(0, 4).map((step) => 
    getImageUrl(step.first_image_url)
  );

  const showGrid = plan.steps.length >= 4;

  return (
    <Link
      to={`/plans/${plan.id}`}
      className="group rounded-lg font-myYekanFaNumRegular overflow-hidden bg-white border border-gray-200 hover:shadow-md transition w-72 flex-shrink-0 flex flex-col h-full"
    >
      {/* بخش تصاویر */}
      <div className="relative h-48 w-full bg-gray-100">
        {showGrid ? (
          <div className="grid grid-cols-2 grid-rows-2 h-full w-full p-1">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="relative overflow-hidden border border-white/20 p-1">
                <img
                  src={images[index] || NoImg}
                  alt={`Destination ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-300 rounded-md"
                  onError={(e) => {
                    e.currentTarget.src = NoImg;
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <img
            src={images[0] || NoImg}
            alt={plan.name}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
            onError={(e) => {
              e.currentTarget.src = NoImg;
            }}
          />
        )}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-bold text-gray-700 flex items-center gap-1 shadow-sm z-10">
          <CalendarDaysIcon className="w-3 h-3" />
          {plan.suggested_season}
        </div>
      </div>
      
      {/* بخش محتوا */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-lg font-myYekanDemibold text-gray-900 mb-2 line-clamp-1">
          {plan.name}
        </h3>
        
        {plan.description && (
          <p className="text-xs text-gray-500 mb-3 line-clamp-2 h-8 text-justify">
            
            <ReactMarkdown>{plan.description}</ReactMarkdown>
          </p>
        )}
        
        <div className="mt-auto">
          <div className="flex items-center font-myYekanFaNumRegular justify-between text-xs text-gray-400 mb-2 border-b pb-1">
            <span>{plan.steps.length} مقصد</span>
          </div>
          
          <div className="space-y-2">
            {plan.steps.slice(0, 3).map((step) => (
              <div key={step.id} className="flex items-start gap-2 text-xs">
                <div className="mt-0.5 min-w-[4px] h-4 rounded-full bg-green-500"></div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-700 truncate w-32">{step.place_name}</span>
                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                      <ClockIcon className="w-3 h-3" />
                      {formatTime(step.start_time)}
                    </span>
                  </div>
                  <div className="text-gray-400 text-[10px] truncate">{step.activity}</div>
                </div>
              </div>
            ))}
            {plan.steps.length > 3 && (
              <div className="text-center text-xs text-green-600 font-medium pt-1">
                +{plan.steps.length - 3} مقصد دیگر
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PlanCard;